import { Target, Palette, Type, MessageCircle, Image, Layers, MapPin, Shield, Wand2, Globe, CheckCircle, Clock, AlertCircle, Users, Activity } from 'lucide-react'

export interface ToolboxItem {
  id: string
  title: string
  description: string
  category: string
  status: 'complete' | 'in-progress' | 'not-started'
  icon: React.ReactNode
  estimatedTime: string
  progress: number
}

export interface ActivityItem {
  id: string
  type: 'toolbox' | 'ai-asset' | 'team' | 'brand'
  action: string
  details: string
  timestamp: string
  user: string
  avatar?: string
}

export interface AIAsset {
  id: string
  title: string
  type: 'logo' | 'social' | 'marketing' | 'template'
  thumbnail: string
  isWatermarked: boolean
  isPro: boolean
}

export const TOOLBOX_CATEGORIES = [
  { name: 'Discovery & Research', color: 'bg-blue-100 text-blue-700', progress: 60 },
  { name: 'Brand Strategy', color: 'bg-purple-100 text-purple-700', progress: 40 },
  { name: 'Concept Development', color: 'bg-green-100 text-green-700', progress: 20 },
  { name: 'Design Execution', color: 'bg-orange-100 text-orange-700', progress: 80 },
  { name: 'Brand Guidelines', color: 'bg-teal-100 text-teal-700', progress: 90 },
  { name: 'Implementation', color: 'bg-red-100 text-red-700', progress: 10 }
]

export const RECENT_ACTIVITY: ActivityItem[] = [
  {
    id: '1',
    type: 'toolbox',
    action: 'Completed Color Palette Generator',
    details: 'Brand color system finalized with 5 primary colors',
    timestamp: '2 hours ago',
    user: 'You',
    avatar: ''
  },
  {
    id: '2',
    type: 'ai-asset',
    action: 'Generated logo variations',
    details: '12 new logo concepts created using AI',
    timestamp: '5 hours ago',
    user: 'AI Assistant',
    avatar: ''
  },
  {
    id: '3',
    type: 'team',
    action: 'Sarah joined the team',
    details: 'Added as Editor with design permissions',
    timestamp: '1 day ago',
    user: 'You',
    avatar: ''
  },
  {
    id: '4',
    type: 'toolbox',
    action: 'Updated Brand Strategy Canvas',
    details: 'Refined value proposition and positioning',
    timestamp: '2 days ago',
    user: 'Sarah Chen',
    avatar: ''
  },
  {
    id: '5',
    type: 'brand',
    action: 'Published brand page',
    details: 'Public guidelines now live at /brand/techflow',
    timestamp: '3 days ago',
    user: 'You',
    avatar: ''
  }
]

export const AI_ASSETS: AIAsset[] = [
  {
    id: '1',
    title: 'Logo Variations',
    type: 'logo',
    thumbnail: '/placeholder-logo.png',
    isWatermarked: true,
    isPro: true
  },
  {
    id: '2',
    title: 'Social Media Templates',
    type: 'social',
    thumbnail: '/placeholder-social.png',
    isWatermarked: true,
    isPro: true
  },
  {
    id: '3',
    title: 'Business Card Design',
    type: 'marketing',
    thumbnail: '/placeholder-card.png',
    isWatermarked: true,
    isPro: true
  }
]

export const TOOLBOX_ITEMS: ToolboxItem[] = [
  {
    id: 'brand-strategy',
    title: 'Brand Strategy Canvas',
    description: 'Define your brand purpose, values, and positioning',
    category: 'Strategy',
    status: 'complete',
    icon: <Target className="w-5 h-5" />,
    estimatedTime: '15 min',
    progress: 100
  },
  {
    id: 'color-palette',
    title: 'Color Palette Generator',
    description: 'Create and refine your brand color system',
    category: 'Design',
    status: 'complete',
    icon: <Palette className="w-5 h-5" />,
    estimatedTime: '10 min',
    progress: 100
  },
  {
    id: 'typography',
    title: 'Typography System',
    description: 'Choose and define your brand fonts and hierarchy',
    category: 'Design',
    status: 'complete',
    icon: <Type className="w-5 h-5" />,
    estimatedTime: '12 min',
    progress: 100
  },
  {
    id: 'voice-tone',
    title: 'Voice & Tone Guide',
    description: 'Establish your brand communication style',
    category: 'Content',
    status: 'complete',
    icon: <MessageCircle className="w-5 h-5" />,
    estimatedTime: '20 min',
    progress: 100
  },
  {
    id: 'logo-guidelines',
    title: 'Logo Guidelines',
    description: 'Define logo usage rules, spacing, and applications',
    category: 'Design',
    status: 'complete',
    icon: <Image className="w-5 h-5" />,
    estimatedTime: '18 min',
    progress: 100
  },
  {
    id: 'mood-board',
    title: 'Mood Board Creator',
    description: 'Collect visual inspiration and define aesthetic direction',
    category: 'Research',
    status: 'complete',
    icon: <Layers className="w-5 h-5" />,
    estimatedTime: '25 min',
    progress: 100
  },
  {
    id: 'brand-positioning',
    title: 'Brand Positioning Template',
    description: 'Define competitive positioning and value propositions',
    category: 'Strategy',
    status: 'not-started',
    icon: <MapPin className="w-5 h-5" />,
    estimatedTime: '30 min',
    progress: 0
  },
  {
    id: 'swot-analysis',
    title: 'SWOT Analysis Board',
    description: 'Analyze strengths, weaknesses, opportunities, and threats',
    category: 'Strategy',
    status: 'not-started',
    icon: <Shield className="w-5 h-5" />,
    estimatedTime: '35 min',
    progress: 0
  }
]

export const getStatusIcon = (status: string) => {
  switch (status) {
    case 'complete':
      return <CheckCircle className="w-4 h-4 text-green-500" />
    case 'in-progress':
      return <Clock className="w-4 h-4 text-blue-500" />
    default:
      return <AlertCircle className="w-4 h-4 text-slate-400" />
  }
}

export const getActivityIcon = (type: string) => {
  switch (type) {
    case 'toolbox':
      return <Target className="w-4 h-4 text-blue-500" />
    case 'ai-asset':
      return <Wand2 className="w-4 h-4 text-purple-500" />
    case 'team':
      return <Users className="w-4 h-4 text-green-500" />
    case 'brand':
      return <Globe className="w-4 h-4 text-teal-500" />
    default:
      return <Activity className="w-4 h-4 text-slate-400" />
  }
}